import { useCallback, useEffect, useState } from 'react';

// Account page: sign-in / sign-up / settings / donate tabs. Sign-in is
// magic-link only in Block 11 (OAuth deferred). Donate page surfaces the
// monthly cost transparency.
export function Account() {
  const [tab, setTab] = useState<'signin' | 'settings' | 'donate'>('signin');

  return (
    <div
      style={{
        maxWidth: 720,
        margin: '0 auto',
        padding: '32px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
      }}
    >
      <header>
        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 28,
            fontWeight: 400,
            margin: 0,
            color: 'var(--fg-1)',
          }}
        >
          Account
        </h1>
      </header>

      <nav style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--line-1)' }}>
        {(['signin', 'settings', 'donate'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className="caps mono"
            style={{
              padding: '8px 14px',
              fontSize: 10,
              color: tab === t ? 'var(--accent-2)' : 'var(--fg-3)',
              borderBottom: tab === t ? '2px solid var(--accent-1)' : '2px solid transparent',
              letterSpacing: '0.08em',
              marginBottom: -1,
            }}
          >
            {t === 'signin' ? 'sign in' : t}
          </button>
        ))}
      </nav>

      {tab === 'signin' && <SignIn />}
      {tab === 'settings' && <Settings />}
      {tab === 'donate' && <Donate />}
    </div>
  );
}

function SignIn() {
  const [mode, setMode] = useState<'login' | 'register' | 'magic'>('login');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {(['login', 'register', 'magic'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className="caps mono"
            style={{
              padding: '4px 10px',
              fontSize: 9.5,
              color: mode === m ? 'var(--accent-2)' : 'var(--fg-3)',
              border: '1px solid',
              borderColor: mode === m ? 'var(--accent-line)' : 'var(--line-1)',
              background: mode === m ? 'var(--accent-bg)' : 'transparent',
              borderRadius: 3,
              letterSpacing: '0.08em',
            }}
          >
            {m === 'login' ? 'sign in' : m === 'register' ? 'create account' : 'magic link'}
          </button>
        ))}
      </div>
      {mode === 'login' && <PasswordForm kind="login" />}
      {mode === 'register' && <PasswordForm kind="register" />}
      {mode === 'magic' && <MagicLinkForm />}
    </div>
  );
}

function PasswordForm({ kind }: { kind: 'login' | 'register' }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setStatus('submitting');
    setError(null);
    try {
      const res = await fetch(`/api/auth/${kind}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.ok === false) {
        throw new Error(prettyAuthError(body?.error) ?? `HTTP ${res.status}`);
      }
      setStatus('success');
      // Reload so the rest of the app picks up /api/auth/me.
      window.location.assign('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus('error');
    }
  }

  const submitting = status === 'submitting' || status === 'success';
  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ color: 'var(--fg-2)', fontSize: 13, margin: 0 }}>
        {kind === 'login'
          ? 'Sign in with the password you set when you created your account.'
          : 'Create an account with email + password. No verification email is sent — pick a password you can remember.'}
      </p>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span className="caps mono" style={{ color: 'var(--fg-3)', fontSize: 9.5 }}>
          email
        </span>
        <input
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
          placeholder="you@example.com"
          style={inputStyle}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span className="caps mono" style={{ color: 'var(--fg-3)', fontSize: 9.5 }}>
          password{kind === 'register' ? ' (min 8 chars)' : ''}
        </span>
        <input
          type="password"
          required
          minLength={kind === 'register' ? 8 : undefined}
          autoComplete={kind === 'login' ? 'current-password' : 'new-password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={submitting}
          style={inputStyle}
        />
      </label>
      <button
        type="submit"
        disabled={submitting || !email || !password}
        className="caps mono"
        style={primaryBtnStyle}
      >
        {status === 'submitting'
          ? kind === 'login'
            ? 'signing in…'
            : 'creating…'
          : status === 'success'
            ? 'redirecting…'
            : kind === 'login'
              ? 'sign in'
              : 'create account'}
      </button>
      {error && <div style={{ color: 'var(--neg)', fontSize: 12 }}>{error}</div>}
    </form>
  );
}

function MagicLinkForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setStatus('sending');
    setError(null);
    try {
      const res = await fetch('/api/auth/magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error ?? `HTTP ${res.status}`);
      setStatus('sent');
      if (typeof j.link_for_dev === 'string') setDevLink(j.link_for_dev);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus('error');
    }
  }

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ color: 'var(--fg-2)', fontSize: 13, margin: 0 }}>
        Email a one-tap link instead. No password needed.
      </p>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span className="caps mono" style={{ color: 'var(--fg-3)', fontSize: 9.5 }}>
          email
        </span>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === 'sending' || status === 'sent'}
          placeholder="you@example.com"
          style={inputStyle}
        />
      </label>
      <button
        type="submit"
        disabled={status === 'sending' || status === 'sent' || !email}
        className="caps mono"
        style={primaryBtnStyle}
      >
        {status === 'sending'
          ? 'sending…'
          : status === 'sent'
            ? 'check your email'
            : 'send magic link'}
      </button>
      {devLink && (
        <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>
          dev link:{' '}
          <a href={devLink} style={{ color: 'var(--accent-2)' }}>
            {devLink}
          </a>
        </div>
      )}
      {error && <div style={{ color: 'var(--neg)', fontSize: 12 }}>error: {error}</div>}
    </form>
  );
}

function prettyAuthError(code: unknown): string | null {
  switch (code) {
    case 'invalid_credentials':
      return 'Wrong email or password.';
    case 'email_taken':
      return 'An account already exists for that email. Sign in instead.';
    case 'password_too_short':
      return 'Password must be at least 8 characters.';
    case 'invalid_email':
      return 'Email looks invalid.';
    default:
      return typeof code === 'string' ? code : null;
  }
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--bg-1)',
  border: '1px solid var(--line-1)',
  borderRadius: 3,
  color: 'var(--fg-1)',
  fontSize: 13.5,
  fontFamily: 'var(--font-mono)',
};

const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  background: 'var(--accent-bg)',
  border: '1px solid var(--accent-line)',
  color: 'var(--accent-2)',
  borderRadius: 3,
  fontSize: 10,
  letterSpacing: '0.08em',
  alignSelf: 'flex-start',
};

function Settings() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <SavedTeams />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <SettingRow label="default regulation" value="Reg M-A" />
        <SettingRow label="theme" value="dark (light mode in a follow-on)" />
        <SettingRow label="sprite style" value="gen 5 (HOME and dex art pending)" />
        <SettingRow label="JP translation" value="on" />
        <SettingRow label="email" value="—" muted />
        <SettingRow label="data export" value="JSON download" link />
      </div>
    </div>
  );
}

type SavedTeam = {
  id: number;
  name: string;
  regulationId: string | null;
  createdAt: string;
  updatedAt: string;
};

function SavedTeams() {
  const [teams, setTeams] = useState<SavedTeam[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const res = await fetch('/api/teams', { credentials: 'include' });
      if (res.status === 401) {
        setErr('sign in to view your saved teams');
        setTeams([]);
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.error ?? `HTTP ${res.status}`);
        return;
      }
      setTeams(body.teams ?? []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function onDelete(id: number) {
    if (!confirm('Delete this saved team? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      const res = await fetch(`/api/teams/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setTeams((prev) => (prev ?? []).filter((t) => t.id !== id));
      } else {
        const body = await res.json().catch(() => ({}));
        setErr(body?.error ?? `delete failed (HTTP ${res.status})`);
      }
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <span
          className="caps mono"
          style={{ color: 'var(--fg-3)', fontSize: 10, letterSpacing: '0.08em' }}
        >
          saved teams
        </span>
        {teams && teams.length > 0 && (
          <span className="mono" style={{ color: 'var(--fg-4)', fontSize: 11 }}>
            {teams.length}
          </span>
        )}
      </div>

      {err && (
        <div className="mono" style={{ fontSize: 12, color: 'var(--fg-3)' }}>
          {err}
        </div>
      )}

      {teams === null && !err && (
        <div className="mono" style={{ fontSize: 11, color: 'var(--fg-4)', opacity: 0.6 }}>
          loading…
        </div>
      )}

      {teams && teams.length === 0 && !err && (
        <div
          style={{
            fontSize: 12.5,
            color: 'var(--fg-3)',
            padding: '10px 12px',
            border: '1px dashed var(--line-1)',
            borderRadius: 4,
            lineHeight: 1.5,
          }}
        >
          You haven't saved any teams yet. Click <span style={{ color: 'var(--fg-2)' }}>save</span>{' '}
          on a team card during a chat to add one here.
        </div>
      )}

      {teams && teams.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {teams.map((t, i) => (
            <div
              key={t.id}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 12,
                padding: '8px 0',
                borderTop: i === 0 ? 'none' : '1px solid var(--line-1)',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 13, color: 'var(--fg-1)' }}>{t.name}</span>
                <span
                  className="mono"
                  style={{ fontSize: 10.5, color: 'var(--fg-4)', letterSpacing: '0.02em' }}
                >
                  {t.regulationId ? `reg ${t.regulationId} · ` : ''}saved{' '}
                  {formatRelativeDate(t.createdAt)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onDelete(t.id)}
                disabled={deletingId === t.id}
                className="caps mono"
                style={{
                  fontSize: 9.5,
                  padding: '3px 8px',
                  background: 'transparent',
                  color: deletingId === t.id ? 'var(--fg-4)' : 'var(--neg)',
                  border: '1px solid var(--line-1)',
                  borderRadius: 3,
                  cursor: 'pointer',
                  letterSpacing: '0.08em',
                }}
              >
                {deletingId === t.id ? 'deleting…' : 'delete'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatRelativeDate(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return iso;
  const seconds = Math.max(0, (Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.floor(minutes)}m ago`;
  const hours = minutes / 60;
  if (hours < 24) return `${Math.floor(hours)}h ago`;
  const days = hours / 24;
  if (days < 30) return `${Math.floor(days)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function SettingRow({
  label,
  value,
  link,
  muted,
}: {
  label: string;
  value: string;
  link?: boolean;
  muted?: boolean;
}) {
  return (
    <div
      style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}
    >
      <span
        className="caps mono"
        style={{ color: 'var(--fg-3)', fontSize: 10, letterSpacing: '0.08em' }}
      >
        {label}
      </span>
      <span
        style={{
          color: muted ? 'var(--fg-4)' : link ? 'var(--accent-2)' : 'var(--fg-1)',
          fontSize: 13,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function Donate() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ color: 'var(--fg-1)', fontSize: 14, lineHeight: 1.6 }}>
        vgcbuddy runs on a small Droplet, embeds JP team reports with Voyage, and uses Claude Sonnet
        for translation, claim extraction, and chat. Hosting is real but small. Surplus funds the
        maker's kid's VGC regionals travel.
      </div>

      <div className="vb-card" style={{ padding: 14 }}>
        <div
          className="caps mono"
          style={{ color: 'var(--fg-3)', fontSize: 10, letterSpacing: '0.1em', marginBottom: 8 }}
        >
          monthly cost (transparent)
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          <CostRow label="Droplet (Postgres + Hono)" amount="$14.40" />
          <CostRow label="Voyage embeddings" amount="$3.10" />
          <CostRow label="Anthropic (chat + translate + extract)" amount="$24.70" />
          <CostRow label="Domain + misc" amount="$2.10" />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingTop: 8,
              marginTop: 4,
              borderTop: '1px solid var(--line-1)',
              fontWeight: 600,
            }}
          >
            <span style={{ color: 'var(--fg-1)' }}>total hosting</span>
            <span className="mono tabular" style={{ color: 'var(--fg-1)' }}>
              $44.30
            </span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span
          className="caps mono"
          style={{ color: 'var(--fg-4)', fontSize: 9.5, letterSpacing: '0.1em' }}
        >
          tip the maker
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <DonateOption label="GitHub Sponsors" href="https://github.com/sponsors/" />
          <DonateOption label="Buy Me a Coffee" href="https://buymeacoffee.com/" />
          <DonateOption label="PayPal" href="https://paypal.me/" />
        </div>
      </div>
    </div>
  );
}

function CostRow({ label, amount }: { label: string; amount: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--fg-2)' }}>{label}</span>
      <span className="mono tabular" style={{ color: 'var(--fg-1)' }}>
        {amount}
      </span>
    </div>
  );
}

function DonateOption({ label, href }: { label: string; href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        background: 'var(--bg-1)',
        border: '1px solid var(--line-1)',
        borderRadius: 4,
        color: 'var(--fg-1)',
      }}
    >
      <span>{label}</span>
      <span style={{ color: 'var(--fg-4)' }}>↗</span>
    </a>
  );
}
